import { useMsal } from '@azure/msal-react';
import { RefreshCw, LogOut, User, Sun, Moon, Eye, EyeOff, Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useEnv, type Environment } from '../../contexts/EnvContext';
import { useImpersonate, type ViewAsRole } from '../../contexts/ImpersonateContext';
import { useCurrentUser } from '../../hooks/useApi';

const envOrder: Environment[] = ['dev', 'stage', 'prod'];
const envLabels: Record<Environment, { label: string; color: string }> = {
  dev: { label: 'DEV', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  stage: { label: 'STAGE', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  prod: { label: 'PROD', color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
};

const roleLabels: Record<string, { label: string; color: string }> = {
  admin: { label: 'Admin', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  marketing: { label: 'Marketing', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
  user: { label: 'User', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
};

interface HeaderProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onOpenMobile: () => void;
}

export function Header({ collapsed, onToggleCollapsed, onOpenMobile }: HeaderProps) {
  const { instance, accounts } = useMsal();
  const queryClient = useQueryClient();
  const { theme, toggleTheme } = useThemeContext();
  const { env, setEnv } = useEnv();
  const { viewAsRole, setViewAsRole, isImpersonating } = useImpersonate();
  const currentUser = useCurrentUser();
  const realIsAdmin = currentUser.data?.isAdmin ?? false;
  const account = accounts[0];

  const cycleEnv = () => {
    const idx = envOrder.indexOf(env);
    const next = envOrder[(idx + 1) % envOrder.length];
    setEnv(next);
    queryClient.invalidateQueries();
  };

  const handleImpersonate = (role: ViewAsRole) => {
    setViewAsRole(role);
  };

  const cfg = envLabels[env];

  return (
    <header className="h-14 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between px-3 sm:px-6 gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <button
          onClick={onOpenMobile}
          className="p-2 -ml-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors lg:hidden"
          aria-label="Otworz menu"
        >
          <Menu className="h-5 w-5" />
        </button>

        <button
          onClick={onToggleCollapsed}
          className="hidden lg:inline-flex p-2 -ml-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          title={collapsed ? 'Rozwin menu' : 'Zwin menu'}
          aria-label={collapsed ? 'Rozwin menu' : 'Zwin menu'}
        >
          {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </button>

        <button
          onClick={cycleEnv}
          className={`px-3 py-1 rounded-full text-xs font-bold tracking-wider transition-colors ${cfg.color} hover:opacity-80`}
          title={`Current: ${cfg.label}. Click to switch.`}
        >
          {cfg.label}
        </button>

        {/* Impersonate — only for real admins; hidden on the narrowest screens */}
        {realIsAdmin && (
          <div className="hidden sm:flex items-center gap-1">
            {isImpersonating ? (
              <>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${roleLabels[viewAsRole!].color} flex items-center gap-1`}>
                  <Eye className="h-3 w-3" />
                  {roleLabels[viewAsRole!].label}
                </span>
                <button
                  onClick={() => handleImpersonate(null)}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded transition-colors"
                  title="Zakoncz impersonation"
                >
                  <EyeOff className="h-3.5 w-3.5" />
                </button>
              </>
            ) : (
              <select
                value=""
                onChange={(e) => handleImpersonate(e.target.value as ViewAsRole)}
                className="text-[10px] text-gray-400 bg-transparent border-none cursor-pointer hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none"
                title="Impersonate role"
              >
                <option value="" disabled>👁 View as...</option>
                <option value="marketing">Marketing</option>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 sm:gap-2 shrink-0">
        <button
          onClick={toggleTheme}
          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
        >
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        <button
          onClick={() => queryClient.invalidateQueries()}
          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw className="h-4 w-4" />
        </button>

        {account && (
          <div className="flex items-center gap-2 text-sm">
            <div className="p-1.5 bg-gray-100 dark:bg-gray-800 rounded-full">
              <User className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
            </div>
            <span className="text-gray-700 dark:text-gray-300 hidden sm:inline">{account.name || account.username}</span>
          </div>
        )}

        <button
          onClick={() => instance.logoutRedirect()}
          className="p-2 text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          title="Sign out"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
}
